import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import { useDispatch, useSelector } from "react-redux";
import CustomTable from "src/components/Table/Table";
import CustomBackdrop from "src/components/CustomBackdrop/CustomBackdrop";

import {
  getCamerasListAction,
  addCameraAction,
  deleteCameraAction,
  updateCameraStatusAction,
  getCaptainsDropdownAction,
  assignCaptainToCameraAction,
} from "src/api/cameras";

export default function Cameras() {
  const dispatch = useDispatch();
  const { camerasList } = useSelector((state: any) => state.cameras);

  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteItem, setDeleteItem] = useState<any>(null);

  // ✅ CAPTAINS STATE
  const [captains, setCaptains] = useState<any[]>([]);

  const [form, setForm] = useState({
    cameraNumber: "",
    cameraKey: "",
    simNumber: "",
    driverId: "",
  });

  // ✅ FETCH DATA
  const fetchData = async () => {
    setLoading(true);
    await dispatch(getCamerasListAction({ pageNo: 1, pageSize: 10 }));
    setLoading(false);
  };

  // ✅ FETCH CAPTAINS
  const fetchCaptains = async () => {
    const res = await dispatch(getCaptainsDropdownAction());
    setCaptains(res || []);
  };

  useEffect(() => {
    fetchData();
    fetchCaptains();
  }, []);

  // ✅ STATUS TOGGLE
  const handleStatusChange = async (row: any, value: number) => {
    await dispatch(updateCameraStatusAction(row.id));
    fetchData();
  };

  // ✅ ASSIGN CAPTAIN (NEW)
  const handleAssignCaptain = async (row: any, captainId: string) => {
    await dispatch(
      assignCaptainToCameraAction({
        cameraId: row.id,
        captainId,
      })
    );
    fetchData();
  };

  // ✅ DELETE
  const confirmDelete = async () => {
    await dispatch(deleteCameraAction(deleteItem.id));
    setDeleteItem(null);
    fetchData();
  };

  // ✅ ADD CAMERA
  const handleSubmit = async () => {
    await dispatch(addCameraAction(form));
    setOpenDialog(false);
    fetchData();
  };

  const columns = [
    { field: "cameraNumber", headerName: "Camera Number", minWidth: 150 },
    { field: "cameraKey", headerName: "Camera Key", minWidth: 150 },
    { field: "simNumber", headerName: "SIM Number", minWidth: 150 },
    { field: "captain", headerName: "Captain", minWidth: 180 },
    { field: "status", headerName: "Status", minWidth: 120 },
    { field: "action", headerName: "Action", minWidth: 120 },
  ];

  const actionContent = (column: any, row: any) => {
    if (column === "status") {
      return (
        <FormControl size="small">
          <Select
            value={row.status}
            onChange={(e) => handleStatusChange(row, Number(e.target.value))}
            sx={{
              fontWeight: 600,
              color: row.status ? "#117C39" : "#BA2222",
              backgroundColor: row.status ? "#dcfce7" : "#fee2e2",
            }}
          >
            <MenuItem value={1}>Active</MenuItem>
            <MenuItem value={0}>Inactive</MenuItem>
          </Select>
        </FormControl>
      );
    }

    // ✅ DRIVER DROPDOWN (NOW FROM API)
    if (column === "captain") {
      return (
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <Select
            value={row.driverId || ""}
            onChange={(e) =>
              handleAssignCaptain(row, e.target.value as string)
            }
            displayEmpty
          >
            <MenuItem value="">
              <span style={{ color: "#9ca3af" }}>Assign Captain</span>
            </MenuItem>

            {captains.map((c) => (
              <MenuItem key={c.value} value={c.value}>
                {c.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (column === "action") {
      return (
        <IconButton onClick={() => setDeleteItem(row)}>
          <DeleteOutlineIcon />
        </IconButton>
      );
    }

    return null;
  };

  return (
    <>
      {loading && <CustomBackdrop open />}

      <Box>
        <Box display="flex" flexDirection="column" gap={1.5} mb={2}>
          <Typography variant="h5" sx={{ lineHeight: "32px" }}>
            Cameras
          </Typography>

          <Button
            variant="outlined"
            startIcon={<PersonAddAltIcon />}
            onClick={() => setOpenDialog(true)}
            sx={{
              alignSelf: "flex-start",
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Add Camera
          </Button>
        </Box>

        <Box mt={3}>
          <CustomTable
            rows={camerasList?.data || []}
            columns={columns}
            total={camerasList?.total || 0}
            fetchData={() => {}}
            resetPagination={() => {}}
            action={actionContent}
          />
        </Box>
      </Box>

      {/* ADD CAMERA */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 600, fontSize: "20px", textAlign: "center", pt: 3 }}>
          Add Camera
        </DialogTitle>

        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <TextField
              label="Camera Number"
              fullWidth
              onChange={(e) => setForm({ ...form, cameraNumber: e.target.value })}
            />

            <TextField
              label="Camera Key"
              fullWidth
              onChange={(e) => setForm({ ...form, cameraKey: e.target.value })}
            />

            <TextField
              label="SIM Number"
              fullWidth
              onChange={(e) => setForm({ ...form, simNumber: e.target.value })}
            />

            {/* ✅ CAPTAIN DROPDOWN */}
            <FormControl fullWidth>
              <Select
                displayEmpty
                value={form.driverId}
                onChange={(e) =>
                  setForm({ ...form, driverId: e.target.value })
                }
              >
                <MenuItem value="">
                  <span style={{ color: "#9ca3af" }}>Select Captain</span>
                </MenuItem>

                {captains.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box display="flex" justifyContent="space-between" mt={3}>
              <Button onClick={() => setOpenDialog(false)} sx={{ color: "#ef4444" }}>
                Cancel
              </Button>

              <Button variant="contained" onClick={handleSubmit}>
                Save
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* DELETE */}
      <Dialog open={!!deleteItem} onClose={() => setDeleteItem(null)}>
        <DialogTitle sx={{ fontWeight: 600 }}>
          Delete Confirmation
        </DialogTitle>

        <DialogContent>
          <Typography mb={3}>
            Are you sure you want to delete{" "}
            <b>{deleteItem?.cameraNumber}</b>?
          </Typography>

          <Box display="flex" justifyContent="flex-end">
            <Button onClick={() => setDeleteItem(null)} sx={{ mr: 2 }}>
              Cancel
            </Button>

            <Button
              variant="contained"
              color="error"
              onClick={confirmDelete}
            >
              Delete
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}